import type { CRMUser, UserRole } from '../types';
import { isCrmTestUser } from './userRoles';

/** Employees, admins, and field staff — no company chip in @mention lists. */
const INTERNAL_MENTION_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  'employee',
  'employee_shop',
  'employee_office',
  'admin',
  'staff',
  'designer',
]);

/**
 * Teammates who belong on every internal comment @ list (leads, ops, marketing, hiring).
 * Designers, architects, and engineers must stay on this list so anyone can @ them.
 */
export const INTERNAL_COMMENT_MENTION_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  'employee',
  'employee_shop',
  'employee_office',
  'admin',
  'staff',
  'designer',
  'architect',
  'engineer',
]);

/** Roles that may appear in TipTap @mention autocomplete when the caller includes them. */
export const MENTION_AUTOCOMPLETE_ROLES: ReadonlySet<UserRole> = new Set<UserRole>([
  'employee',
  'employee_shop',
  'employee_office',
  'admin',
  'staff',
  'designer',
  'architect',
  'engineer',
  'cpa',
  'supplier',
]);

export function isInternalCommentMentionUser(
  user: Pick<CRMUser, 'isActive' | 'role'>,
): boolean {
  return !!user.isActive && INTERNAL_COMMENT_MENTION_ROLES.has(user.role);
}

export function isMentionAutocompleteRole(role: UserRole | string | undefined): boolean {
  return !!role && MENTION_AUTOCOMPLETE_ROLES.has(role as UserRole);
}

/**
 * TipTap / mention dropdown eligibility.
 * CRM test accounts stay in comment threads; only admins may @mention them.
 */
export function canAppearInMentionAutocomplete(
  user: Pick<CRMUser, 'isActive' | 'role' | 'isTestUser'>,
  viewerRole?: UserRole | string | null,
): boolean {
  if (!user.isActive) return false;
  if (!isMentionAutocompleteRole(user.role)) return false;
  if (isCrmTestUser(user) && viewerRole !== 'admin') return false;
  return true;
}

/** Strip test users for everyone except admins; admins also get any leftover mentionable test accounts. */
export function applyCrmTestUserMentionPolicy<T extends Pick<CRMUser, 'uid' | 'isActive' | 'role' | 'isTestUser'>>(
  candidates: T[],
  allUsers: T[],
  viewerRole?: UserRole | string | null,
): T[] {
  if (viewerRole === 'admin') {
    const seen = new Set(candidates.map((u) => u.uid));
    const extras = allUsers.filter(
      (u) => isCrmTestUser(u) && canAppearInMentionAutocomplete(u, 'admin') && !seen.has(u.uid),
    );
    return extras.length ? [...candidates, ...extras] : candidates;
  }
  return candidates.filter((u) => !isCrmTestUser(u));
}

/** True for suppliers, architects, engineers, CPAs, etc. — show org/company beside the name. */
export function shouldShowMentionAffiliation(role: UserRole | string | undefined): boolean {
  if (!role) return false;
  return !INTERNAL_MENTION_ROLES.has(role as UserRole);
}

/**
 * Company / org label for the @mention popover.
 * Suppliers prefer `supplierOrgs.name` via the id map; others use `users.company`.
 */
export function mentionAffiliationLabel(
  user: Pick<CRMUser, 'role' | 'company' | 'supplierOrgId'>,
  supplierOrgNameById?: Map<string, string> | null,
): string | null {
  if (!shouldShowMentionAffiliation(user.role)) return null;
  if (user.role === 'supplier' && user.supplierOrgId && supplierOrgNameById) {
    const orgName = supplierOrgNameById.get(user.supplierOrgId)?.trim();
    if (orgName) return orgName;
  }
  if (user.role === 'architect') {
    return user.company?.trim() || 'Architect';
  }
  const company = user.company?.trim();
  return company || null;
}
